/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = Number(this.dataset.currentPage || 1) + 1;
      this.scrollSaveTimer = null;
      this.boundScheduleStateSave = this.scheduleStateSave.bind(this);
      this.boundSaveState = this.saveState.bind(this);
      this.boundProductCardClick = this.onProductCardClick.bind(this);
      this.boundMaybeLoadInfinite = this.maybeLoadInfinite.bind(this);
      this.endMessage = null;
      this.lastDynamicLoadAt = 0;
    }
    connectedCallback() {
      const usesLoadMore = this.classList.contains('pagination-type--loadmore'),
        usesInfinite = this.classList.contains('pagination-type--infinite'),
        usesDynamicLoading = usesLoadMore || usesInfinite;

      if (!usesDynamicLoading || !this.grid) {
        return;
      }
      this.ensureEndMessage();
      this.storageKey = this.getStorageKey();
      this.bindStateEvents();
      this.restoreState().then(() => {
        if (this.isLastPage()) {
          this.disablePagination(true);
          return;
        }
        if (usesLoadMore) {
          this.loadMore();
        }
        if (usesInfinite) {
          this.infinite();
        }
      });

    }
    disconnectedCallback() {
      this.unbindStateEvents();
      if (this.observer) {
        this.observer.unobserve(this);
      }
      window.removeEventListener('scroll', this.boundMaybeLoadInfinite);
    }
    addUrlParam(search, key, value = this.i) {
      var newParam = key + '=' + value,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    getStorageKey() {
      var searchParams = new URLSearchParams(window.location.search),
        searchKey;
      searchParams.delete('page');
      searchKey = searchParams.toString();
      return 'pagination-theme:' + window.location.pathname + (searchKey ? '?' + searchKey : '');
    }
    getStoredState() {
      if (!this.storageKey) {
        return null;
      }
      try {
        return JSON.parse(window.sessionStorage.getItem(this.storageKey)) || null;
      } catch (error) {
        return null;
      }
    }
    bindStateEvents() {
      if (this.stateEventsBound) {
        return;
      }
      this.stateEventsBound = true;
      window.addEventListener('scroll', this.boundScheduleStateSave, { passive: true });
      window.addEventListener('pagehide', this.boundSaveState);
      this.grid.addEventListener('click', this.boundProductCardClick);
    }
    unbindStateEvents() {
      if (!this.stateEventsBound) {
        return;
      }
      this.stateEventsBound = false;
      window.removeEventListener('scroll', this.boundScheduleStateSave);
      window.removeEventListener('pagehide', this.boundSaveState);
      this.grid.removeEventListener('click', this.boundProductCardClick);
      if (this.scrollSaveTimer) {
        clearTimeout(this.scrollSaveTimer);
      }
    }
    scheduleStateSave() {
      if (this.scrollSaveTimer) {
        clearTimeout(this.scrollSaveTimer);
      }
      this.scrollSaveTimer = setTimeout(() => {
        this.saveState();
      }, 150);
    }
    getLoadedPage() {
      return Math.max(Number(this.dataset.currentPage || 1), this.i - 1);
    }
    getCurrentScrollY() {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }
    saveState(extraState = {}) {
      if (!this.storageKey) {
        return;
      }
      try {
        var state = Object.assign({}, this.getStoredState(), {
          page: this.getLoadedPage(),
          scrollY: this.getCurrentScrollY(),
          timestamp: Date.now()
        }, extraState);
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (error) {
        // Storage can be unavailable in private browsing modes.
      }
    }
    normalizeUrl(url) {
      try {
        var normalizedUrl = new URL(url, window.location.href);
        if (normalizedUrl.origin !== window.location.origin) {
          return null;
        }
        return normalizedUrl.pathname + normalizedUrl.search;
      } catch (error) {
        return null;
      }
    }
    findProductCard(productUrl) {
      if (!productUrl) {
        return null;
      }
      var links = this.grid.querySelectorAll('product-card .product-featured-image-link[href], product-card .product-card-title[href]');
      for (var i = 0; i < links.length; i++) {
        if (this.normalizeUrl(links[i].href) === productUrl) {
          return links[i].closest('product-card');
        }
      }
      return null;
    }
    ensureEndMessage() {
      this.endMessage = this.querySelector('.pagination-end-of-list');
      if (!this.endMessage) {
        this.endMessage = document.createElement('span');
        this.endMessage.className = 'pagination-end-of-list';
        this.endMessage.setAttribute('role', 'status');
        this.endMessage.textContent = 'End of list';
        this.appendChild(this.endMessage);
      }
      this.endMessage.hidden = true;
    }
    showEndMessage() {
      if (!this.endMessage) {
        this.ensureEndMessage();
      }
      this.endMessage.hidden = false;
    }
    getRestoreScrollY(state, fallbackScrollY) {
      var productCard = this.findProductCard(state.productUrl);
      if (!productCard || typeof state.productTop !== 'number') {
        return fallbackScrollY;
      }
      return Math.max(0, this.getCurrentScrollY() + productCard.getBoundingClientRect().top - state.productTop);
    }
    restoreScrollPosition(state, fallbackScrollY) {
      var scrollToSavedPosition = () => {
        var scrollY = this.getRestoreScrollY(state, fallbackScrollY);
        window.scrollTo(0, scrollY);
        this.saveState({
          pendingRestore: false,
          scrollY: scrollY
        });
      };

      scrollToSavedPosition();
      window.requestAnimationFrame(scrollToSavedPosition);
      setTimeout(scrollToSavedPosition, 250);
    }
    onProductCardClick(event) {
      if (!event.target.closest) {
        return;
      }
      var link = event.target.closest('a[href]'),
        productCard = link ? link.closest('product-card') : null,
        productUrl = link ? this.normalizeUrl(link.href) : null;
      if (!link || !this.grid.contains(link) || !productCard || !productUrl) {
        return;
      }
      if (!link.classList.contains('product-featured-image-link') && !link.classList.contains('product-card-title')) {
        return;
      }
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (link.target && link.target !== '_self') {
        return;
      }
      this.saveState({
        pendingRestore: true,
        productUrl: productUrl,
        productTop: productCard.getBoundingClientRect().top
      });
    }
    restoreState() {
      var state = this.getStoredState(),
        maxAge = 30 * 60 * 1000;
      if (!state || !state.pendingRestore || Date.now() - Number(state.timestamp || 0) > maxAge) {
        return Promise.resolve();
      }
      var targetPage = Number(state.page || 1),
        targetScrollY = Number(state.scrollY || 0),
        currentPage = this.getLoadedPage(),
        restore = Promise.resolve();

      this.restoring = true;
      for (let page = currentPage + 1; page <= targetPage; page++) {
        restore = restore.then(() => {
          return this.loadProducts({ page: page, restore: true, animate: false });
        });
      }

      return restore.catch(() => {
        return false;
      }).then(() => {
        this.restoring = false;
        window.requestAnimationFrame(() => {
          this.restoreScrollPosition(state, targetScrollY);
        });
      });
    }
    isLastPage() {
      const currentPage = Number(this.dataset.currentPage),
        pages = Number(this.dataset.pages);

      return currentPage && pages && currentPage >= pages;
    }
    hasNextPage(page = this.i) {
      const pages = Number(this.dataset.pages);

      return !pages || page <= pages;
    }
    disablePagination(showEndMessage = false) {
      if (this.observer) {
        this.observer.unobserve(this);
      }
      window.removeEventListener('scroll', this.boundMaybeLoadInfinite);
      this.removeAttribute('loading');
      if (showEndMessage) {
        this.style.display = '';
        this.removeAttribute('aria-hidden');
        this.showEndMessage();
      } else {
        this.style.display = 'none';
        this.setAttribute('aria-hidden', true);
      }

      if (this.button) {
        this.button.setAttribute('disabled', true);
        this.button.setAttribute('aria-disabled', true);
        this.button.classList.add('visually-hidden');
      }
    }
    updatePaginationState(pagination) {
      if (!pagination) {
        this.disablePagination();
        return;
      }

      this.dataset.currentPage = pagination.dataset.currentPage;
      this.dataset.pages = pagination.dataset.pages;

      if (pagination.dataset.nextUrl) {
        this.dataset.nextUrl = pagination.dataset.nextUrl;
      } else {
        delete this.dataset.nextUrl;
      }

      if (this.isLastPage()) {
        this.disablePagination(true);
      }
    }
    loadBadges() {
      if (typeof StampedFn !== "undefined" && typeof StampedFn.loadBadges !== "undefined") {
        StampedFn.loadBadges();
      }
    }
    loadMore() {
      let base = this;
      if (!this.button) {
        return;
      }
      this.button.addEventListener('click', function () {
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    maybeLoadInfinite() {
      if (this.restoring || this.getAttribute('loading') || !this.hasNextPage()) {
        return;
      }
      if (Date.now() - this.lastDynamicLoadAt < 300) {
        return;
      }
      if (this.getBoundingClientRect().top <= window.innerHeight + 600) {
        this.loadProducts();
      }
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (!base.restoring && (entries[0].isIntersecting || entries[0].intersectionRatio > 0)) {
          base.loadProducts();
        }
      }, {
        rootMargin: '600px 0px 600px 0px',
        threshold: 0
      });
      base.observer.observe(base);
      window.addEventListener('scroll', base.boundMaybeLoadInfinite, { passive: true });
      base.maybeLoadInfinite();
    }
    loadProducts(options = {}) {
      let base = this,
        page = options.page || base.i,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page', page);
      if (base.getAttribute('loading')) {
        return Promise.resolve(false);
      }
      if (!base.hasNextPage(page)) {
        base.disablePagination(true);
        return Promise.resolve(false);
      }
      base.i = page + 1;
      base.setAttribute('loading', true);
      return fetch(url)
        .then(response => response.text())
        .then((responseText) => {
          const html = responseText;
          const productsRendered = base.renderProducts(html, url, options);

          if (!productsRendered) {
            base.i = page;
          }
          if (productsRendered) {
            base.lastDynamicLoadAt = Date.now();
            base.saveState();
          }
          if (productsRendered && !options.restore) {
            dispatchCustomEvent('pagination:page-changed', {
              url: url
            });
          }
          return productsRendered;
        })
        .catch(() => {
          base.i = page;
          base.removeAttribute('loading');
          return false;
        });
    }
    renderProducts(html, url, options = {}) {
      let base = this,
        parsedHTML = new DOMParser().parseFromString(html, 'text/html'),
        grid_to_replace = parsedHTML.getElementById('product-grid'),
        pagination_to_replace = parsedHTML.querySelector('pagination-theme[data-section="' + this.dataset.section + '"]');

      if (!grid_to_replace) {
        base.disablePagination();
        return false;
      }
      let products = grid_to_replace.querySelectorAll('.column');

      if (!products.length) {
        base.disablePagination();
        return false;
      }

      for (var i = 0; i < products.length; i++) {
        this.grid.appendChild(products[i]);
      }
      base.updatePaginationState(pagination_to_replace);
      if (this.animations_enabled && options.animate !== false) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.removeAttribute('loading');
            base.loadBadges();
          }
        });
      } else {
        base.removeAttribute('loading');
        base.loadBadges();
      }
      return true;
    }
  }
  customElements.define('pagination-theme', Pagination);
}
